/**
 * Workspace members + invites panel (FRIDAY-65).
 *
 * One implementation, two surfaces: the workspace settings "Members" tab and
 * the `/w/:wsId/share` screen. The permission rules mirror the server:
 * `requireWorkspaceManager` (owner + admin) may invite, change roles and
 * remove members; everybody else sees the list read-only.
 *
 * The server is the authority on the last owner — it answers 409
 * `LAST_OWNER` — and that message is surfaced verbatim instead of being
 * swallowed.
 */

import { useState } from 'react'
import { ApiError } from '@/lib/api'
import { toast } from '@/features/feedback/toast'
import { pickDisplay, useProfiles } from '@/features/profiles/profiles'
import { useAuth } from '@/features/auth/authContext'
import { ConfirmDialog } from '@/components/feedback'
import {
  useCreateWorkspaceInvite,
  useRemoveWorkspaceMember,
  useRevokeWorkspaceInvite,
  useUpdateWorkspaceMember,
  useWorkspaceInvites,
  useWorkspaceMembers,
  type WorkspaceInviteCreateResponse,
  type WorkspaceInviteRole,
} from '@/features/workspaces/workspaces'
import { cn } from '@/lib/utils'
import { UserAvatar } from '@/components/user/UserAvatar'

const INVITE_ROLES: { value: WorkspaceInviteRole; label: string; desc: string; mapped: string }[] = [
  {
    value: 'lead',
    label: 'Lead',
    desc: 'Full control of the workspace. Can rename, archive, invite others.',
    mapped: 'admin',
  },
  {
    value: 'contributor',
    label: 'Contributor',
    desc: 'Reads everything. Contributes to projects they have access to.',
    mapped: 'member',
  },
  {
    value: 'viewer',
    label: 'Viewer',
    desc: 'Read-only.',
    mapped: 'viewer',
  },
]

const MEMBER_ROLES: Array<{ value: 'admin' | 'member' | 'viewer'; label: string }> = [
  { value: 'admin', label: 'Lead (admin)' },
  { value: 'member', label: 'Contributor (member)' },
  { value: 'viewer', label: 'Viewer' },
]

export interface WorkspaceMembersPanelProps {
  wsId: string
  /** Used in the invite copy, e.g. "every project in Acme". */
  workspaceName?: string
}

export function WorkspaceMembersPanel({ wsId, workspaceName }: WorkspaceMembersPanelProps) {
  const { user } = useAuth()
  const membersQ = useWorkspaceMembers(wsId)
  const members = membersQ.data ?? []
  const { profiles } = useProfiles(members.map((m) => m.userId))

  const myMember = members.find((m) => m.userId === user?.id)
  const canManage = myMember?.role === 'owner' || myMember?.role === 'admin'

  // The pending-invite list is manager-only on the server, so the request is
  // not sent at all for a plain member or a viewer.
  const invitesQ = useWorkspaceInvites(wsId, canManage)
  const createInvite = useCreateWorkspaceInvite(wsId)
  const revokeInvite = useRevokeWorkspaceInvite(wsId)
  const updateMember = useUpdateWorkspaceMember(wsId)
  const removeMember = useRemoveWorkspaceMember(wsId)

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<WorkspaceInviteRole>('contributor')
  const [justInvited, setJustInvited] = useState<WorkspaceInviteCreateResponse | null>(null)
  const [pendingRemove, setPendingRemove] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (membersQ.isLoading) {
    return (
      <p className="text-surface-500 p-4 text-sm" role="status">
        Loading members…
      </p>
    )
  }

  if (membersQ.isError) {
    return (
      <div className="p-4" role="alert">
        <p className="text-sm text-red-600 dark:text-red-300">Could not load the members of this workspace.</p>
        <button type="button" className="btn-ghost mt-2" onClick={() => void membersQ.refetch()}>
          Retry
        </button>
      </div>
    )
  }

  const invites = invitesQ.data ?? []

  return (
    <div className="space-y-4">
      {!canManage && (
        <p className="text-surface-500 text-xs">
          Only workspace owners and admins can invite people or change roles. You are seeing the member list
          read-only.
        </p>
      )}

      {error && (
        <p role="alert" className="rounded-md border border-red-300 p-3 text-sm text-red-600 dark:border-red-800 dark:text-red-300">
          {error}
        </p>
      )}

      {/* Invite by email */}
      <section className="card p-4" aria-label="Invite by email">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">
          Invite by email
        </h2>
        <p className="mb-3 text-xs text-surface-500">
          Send an invite link. They must already have a Friday account (or sign up). Invites expire in 14 days.
          {workspaceName ? ` Anyone who accepts gets access to every project in ${workspaceName}.` : ''}
        </p>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={async (e) => {
            e.preventDefault()
            if (!email.trim() || !canManage) return
            setError(null)
            try {
              const data = await createInvite.mutateAsync({ email: email.trim(), role })
              setJustInvited(data)
              setEmail('')
              toast('Invite created', { kind: 'success' })
            } catch (err) {
              const msg = err instanceof ApiError ? err.message : (err as Error).message
              setError(msg)
              toast(msg, { kind: 'error' })
            }
          }}
        >
          <label className="flex-1 min-w-[16rem]">
            <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Email</span>
            <input
              type="email"
              required
              disabled={!canManage}
              className="input mt-1"
              placeholder="teammate@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Role</span>
            <select
              className="input mt-1"
              value={role}
              disabled={!canManage}
              onChange={(e) => setRole(e.target.value as WorkspaceInviteRole)}
            >
              {INVITE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label} — {r.desc}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canManage || createInvite.isPending || !email.trim()}
          >
            {createInvite.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </form>

        {justInvited && (
          <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            <div className="mb-1 font-medium text-amber-700 dark:text-amber-300">
              Invite link (copy &amp; send)
            </div>
            <code className="block break-all rounded bg-amber-500/10 p-2 font-mono">
              {window.location.origin}
              {justInvited.inviteUrl}
            </code>
            <button
              className="mt-2 text-amber-700 dark:text-amber-300 hover:underline"
              onClick={() => {
                navigator.clipboard?.writeText(window.location.origin + justInvited.inviteUrl)
                toast('Copied', { kind: 'success' })
              }}
            >
              Copy link
            </button>
          </div>
        )}
      </section>

      {/* Pending invites */}
      {canManage && (
        <section className="card divide-y divide-surface-200 dark:divide-surface-700" aria-label="Pending invites">
          <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-surface-500">
            Pending invites
          </div>
          {invitesQ.isLoading && (
            <div className="px-4 py-3 text-sm text-surface-500" role="status">
              Loading pending invites…
            </div>
          )}
          {invitesQ.isError && (
            <div className="px-4 py-3 text-sm" role="alert">
              <span className="text-red-600 dark:text-red-300">Could not load the pending invites. </span>
              <button type="button" className="btn-ghost" onClick={() => void invitesQ.refetch()}>
                Retry
              </button>
            </div>
          )}
          {!invitesQ.isLoading && !invitesQ.isError && invites.length === 0 && (
            <div className="px-4 py-3 text-sm text-surface-500">No pending invites.</div>
          )}
          {invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <div className="min-w-0">
                <div className="truncate font-medium">{inv.email}</div>
                <div className="text-xs text-surface-500">
                  {inv.role} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                </div>
                <div className="text-[10px] text-surface-500">
                  Invites cannot be resent — revoke this one and create a new invite instead.
                </div>
              </div>
              <button
                onClick={() => {
                  revokeInvite.mutate(inv.id, {
                    onSuccess: () => toast('Invite revoked', { kind: 'success' }),
                    onError: (e) => {
                      const msg = e instanceof ApiError ? e.message : 'Failed to revoke'
                      setError(msg)
                      toast(msg, { kind: 'error' })
                    },
                  })
                }}
                className="btn-ghost text-xs text-red-600 dark:text-red-300"
                disabled={revokeInvite.isPending}
              >
                Revoke
              </button>
            </div>
          ))}
        </section>
      )}

      {/* Members */}
      <section className="card divide-y divide-surface-200 dark:divide-surface-700" aria-label="Members">
        <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-surface-500">
          Members ({members.length})
        </div>
        {members.length === 0 && <div className="px-4 py-3 text-sm text-surface-500">No members yet.</div>}
        {members.map((m) => {
          const profile = profiles[m.userId]
          const display = pickDisplay(m.userId, profile)
          const isSelf = m.userId === user?.id
          return (
            <div key={m.userId} className="flex items-center justify-between px-4 py-3 text-sm">
              <div className="flex min-w-0 items-center gap-3">
                <UserAvatar
                  name={display.displayName}
                  email={display.email}
                  sources={display.avatarSources}
                  className="h-7 w-7 text-[10px]"
                  pixelSize={56}
                  decorative
                />
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {display.displayName}
                    {isSelf && <span className="ml-1 text-[10px] text-surface-500">(you)</span>}
                  </div>
                  <div className="truncate text-[10px] text-surface-500">{m.userId}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  aria-label={`Role for ${display.displayName}`}
                  className={cn('input py-0.5 text-xs')}
                  value={m.role}
                  disabled={!canManage || m.role === 'owner'}
                  title={m.role === 'owner' ? 'Only workspace owners can change ownership' : undefined}
                  onChange={(e) => {
                    setError(null)
                    updateMember.mutate(
                      { userId: m.userId, role: e.target.value as 'admin' | 'member' | 'viewer' },
                      {
                        onSuccess: () => toast('Role updated', { kind: 'success' }),
                        onError: (e2) => {
                          const msg = e2 instanceof ApiError ? e2.message : 'Failed to update role'
                          setError(msg)
                          toast(msg, { kind: 'error' })
                        },
                      },
                    )
                  }}
                >
                  {m.role === 'owner' ? (
                    <option value="owner">Owner</option>
                  ) : (
                    MEMBER_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))
                  )}
                </select>
                {canManage && m.role !== 'owner' && (
                  <button onClick={() => setPendingRemove(m.userId)} className="btn-ghost text-xs text-red-600 dark:text-red-300">
                    Remove
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </section>

      <ConfirmDialog
        open={!!pendingRemove}
        title="Remove this member?"
        body={
          <p className="text-sm text-surface-600 dark:text-surface-300">
            They will lose access to this workspace and all of its projects. Their tasks stay intact and can be
            reassigned by an admin later.
          </p>
        }
        confirmLabel="Remove member"
        destructive
        busy={removeMember.isPending}
        onClose={() => !removeMember.isPending && setPendingRemove(null)}
        onConfirm={async () => {
          if (!pendingRemove) return
          try {
            await removeMember.mutateAsync(pendingRemove)
            toast('Member removed', { kind: 'success' })
            setPendingRemove(null)
            setError(null)
          } catch (e) {
            // 409 LAST_OWNER lands here — show the server's own wording.
            const msg = e instanceof ApiError ? e.message : 'Failed to remove'
            setError(msg)
            toast(msg, { kind: 'error' })
          }
        }}
      />
    </div>
  )
}
